import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../utility/Utility";
import CustomLoader from "../../Components/CustomLoader/CustomLoader";
import moment from 'moment';

function XOXOOrderHistory() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const rowsPerPage = 10;

  const fetchOrderHistory = async (page = 1, search = "", startDate = "", endDate = "") => {
    setLoading(true);
    try {
      // Using the Xoxoday API format provided
      const response = await postData("api/xoxoday/vouchers/admin-order-history", {
        startDate: startDate || "2024-08-01",
        endDate: endDate || "2024-08-31",
        limit: rowsPerPage,
        page: page,
        search: search
      });

      if (response?.success) {
        setOrders(response.data || []);
        setTotalPages(response.pagination?.totalPages || 0);
      } else {
        toast.error(response?.message || "Failed to fetch order history");
      }
    } catch (error) {
      console.error("Fetch order history error:", error);
      setOrders([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderHistory(currentPage + 1, searchTerm, startDate, endDate);
  }, [currentPage, searchTerm, startDate, endDate]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
    fetchOrderHistory(selected + 1, searchTerm, startDate, endDate);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchOrderHistory(1, searchTerm, startDate, endDate);
  };

  const handleReset = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(0);
    fetchOrderHistory(1, "", "", "");
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">XOXO Order History</h4>
              <p className="fw-semibold my-2">Order History Management</p>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-secondary"
                onClick={() => navigate("/xoxo-dashboard")}
                disabled={loading}
              >
                <i className="mdi mdi-arrow-left me-1"></i>
                Back to Dashboard
              </button>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/xoxo-payment-history")}
                disabled={loading}
              >
                <i className="mdi mdi-credit-card me-1"></i>
                Payment History
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-12 col-lg-3">
                        <input
                          type="text"
                          className="form-control ps-4"
                          placeholder="Search by order ID or product..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="col-md-12 col-lg-2">
                        <input
                          type="date"
                          className="form-control"
                          placeholder="Start Date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>
                      <div className="col-md-12 col-lg-2">
                        <input
                          type="date"
                          className="form-control"
                          placeholder="End Date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </div>
                      <div className="col-md-12 col-lg-3 d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          <i className="mdi mdi-magnify me-1"></i>
                          Search
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          <i className="mdi mdi-refresh me-1"></i>
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Order ID</th>
                            <th>Product Name</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Order Date</th>
                            <th>Recipient Email</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.length > 0 ? (
                            orders.map((order, index) => (
                              <tr key={order.id || order._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td>{order.orderId || order.id}</td>
                                <td>{order.productName || order.product_name}</td>
                                <td>₹{order.amount?.toLocaleString('en-IN')}</td>
                                <td>
                                  <span className={`badge text-capitalize bg-${order.status === 'completed' ? 'success' :
                                      order.status === 'pending' ? 'warning' :
                                        order.status === 'failed' ? 'danger' : 'secondary'
                                    }`}>
                                    {order.status}
                                  </span>
                                </td>
                                <td>
                                  {order.orderDate ?
                                    moment(order.orderDate).format('DD/MM/YYYY') :
                                    order.createdAt ?
                                      moment(order.createdAt).format('DD/MM/YYYY') :
                                      '-'
                                  }
                                </td>
                                <td>{order.recipientEmail || order.recipient_email || '-'}</td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-outline-info"
                                    title="View Details"
                                  >
                                    <i className="mdi mdi-eye"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            !loading && (
                              <tr>
                                <td colSpan="8" className="text-center py-4">
                                  No orders found
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={<i className="mdi mdi-chevron-left"></i>}
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default XOXOOrderHistory;
